<template>
  <div class="flex h-[52px] w-full flex-row items-center gap-3 separate-rows pl-1">
    <CheckBox :id="dueDateItem.id" v-model="completed" data-testid="due-date-checkbox" class="text-main" />

    <span
      v-if="urgencyBadge"
      class="inline-block rounded-sm px-2 py-1 text-label-s-bold text-inverse uppercase"
      :class="urgencyBadge.color"
      data-testid="urgency-badge"
    >
      {{ urgencyBadge.title }}
    </span>

    <div class="grow txt-body-s-semibold" data-testid="dashboard-dues-title">
      <span v-if="identifier">{{ t('Booth.headline', { name: identifier }) }}: </span>
      {{ title }}

      <span v-if="dueAt" class="txt-body-s-regular text-main" :class="{ 'text-error': !completed && isOverdue }">
        {{ t('DueDateItem.completeBy', { dueDate: d(dueAt, 'dateShort') }) }}
      </span>
    </div>

    <RouterLink v-if="ctaRoute" :to="ctaRoute" class="btn-link-neutral-m" data-testid="cta-label">
      {{ t('DueDateItem.ctaLabel') }}
    </RouterLink>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { type DueDateItem as DueDateItemType, useBoothBasicsQuery, BoothTypeEnum } from '@/gql/myomr'
import { toTypeAndId } from '@/helpers/globalIdHelper'
import { getLocalizedAttribute } from '@/helpers/localizedAttribute'
import { urlifyGlobalId } from '@/helpers/urlifyGlobalId'

const { locale, t, d } = useI18n()

const dueSoonInterval = 1000 * 60 * 60 * 24 * 5 // 5 days

const props = defineProps<{
  dueDateItem: DueDateItemType
}>()

// type-based
const emit = defineEmits<{
  (e: 'completed', id: string, completed: boolean): void
}>()

const completed = ref(props.dueDateItem.completed)

const dueAt = computed(() => {
  return props.dueDateItem.dueAt ? Date.parse(props.dueDateItem.dueAt) : null
})

const isDueSoon = computed(() => {
  return dueDelta.value && dueDelta.value <= dueSoonInterval && dueDelta.value >= 0
})

const isOverdue = computed(() => {
  return dueDelta.value && dueDelta.value <= dueSoonInterval && dueDelta.value < 0
})

const dueDelta = computed(() => {
  return dueAt.value ? dueAt.value - Date.now() : null
})

const title = computed(() =>
  getLocalizedAttribute({
    de: props.dueDateItem.titleDe,
    en: props.dueDateItem.titleEn,
    fallback: '',
    locale: locale.value,
  }),
)

const urgencyBadge = computed(() => {
  if (props.dueDateItem.completed) return null

  if (isOverdue.value) {
    return { title: t('DueDateItem.overdue'), color: 'bg-error-strong' }
  } else if (isDueSoon.value) {
    return { title: t('DueDateItem.urgent'), color: 'bg-warning-strong' }
  }

  return null
})

const resourceTypeAndId = computed(() => {
  if (!props.dueDateItem.resourceId) return null

  return toTypeAndId(props.dueDateItem.resourceId)
})
const resourceType = computed(() => resourceTypeAndId.value?.type)
const resourcePlainId = computed(() => resourceTypeAndId.value?.id)
const boothQueryEnabled = computed(() => Object.values(BoothTypeEnum).includes(resourceType.value as BoothTypeEnum))
const { result: boothResult, loading: boothLoading } = useBoothBasicsQuery(() => ({ boothId: props.dueDateItem.resourceId! }), {
  enabled: boothQueryEnabled,
})
const identifier = computed(() => {
  if (!boothQueryEnabled.value || boothLoading.value) return null

  const fallbackId = resourcePlainId.value ? `#${resourcePlainId.value}` : null
  const node = boothResult.value?.node

  if (!node || node.__typename !== 'Booth') return fallbackId
  return node.boothNumber || fallbackId
})

const signableId = computed(() => {
  if (!props.dueDateItem.resourceId) return null

  return urlifyGlobalId(props.dueDateItem.resourceId)
})
const ctaRoute = computed(() => {
  switch (props.dueDateItem.resourceType) {
    case 'PartnerCompany':
      return { name: 'partnerCompanyEdit' }
    case 'EventList':
      return { name: 'events' }
    case 'BoothList':
      return { name: 'documents' }
    case 'Booth':
    case 'Document': {
      if (!signableId.value) return null

      return { name: 'documentDetails', params: { signableId: signableId.value } }
    }
    case 'SpeakerList':
      return { name: 'speakerList' }
    default:
      return null
  }
})

watch(completed, (completed, _prevCompleted) => {
  emit('completed', props.dueDateItem.id, completed)
})
</script>
